import { useMaxAvailableHeight } from "@redotech/react-util/available-height";
import { genericMemo } from "@redotech/react-util/component";
import { useScrolled } from "@redotech/react-util/scroll";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  headerColorStyle,
  RedoTableProps,
  sizeToTemplateHeaderRowHeight,
  sizeToTemplateRowHeight,
  tableDimensionToGridTemplateStyles,
} from "./redo-table";
import * as redoTableCellsCss from "./redo-table-cells.module.css";
import { RedoTableExpandedRow, RedoTableRow } from "./redo-table-row";
import * as coreRedoTableCss from "./redo-table.module.css";
import * as redoTableCss from "./redo-virtual-table.module.css";

// TODO: joe - make this the default table behavior
// A virtual table is just a table that only renders the rows in view + a few rows above and below
export const RedoVirtualTable = genericMemo(function RedoVirtualTable<DATA>({
  size = "sm",
  layout: { columns, headerColor = "grey", standalone = false },
  rowsOfData,
  onRowClick,
  onContextMenu,
  onRowHovered,
  isRowClickable,
  rowsSelected,
  onVisibleHandlers,
  emptyStateContent = null,
  loadingStateContent = null,
  additionalClasses,
  hideHeader,
  hideHeaderWhenEmpty,
  footerRef,
  rowsExpansions,
  aggregationRow,
  maxAvailableHeight,
  fillContainer = false,
  fillStandaloneBody = false,
}: RedoTableProps<DATA> & {
  fillContainer?: boolean;
  footerRef?: React.RefObject<HTMLDivElement>;
}) {
  const styleForGridColTemplate = columns
    .map((column) =>
      tableDimensionToGridTemplateStyles(column.widthMode || { type: "fill" }),
    )
    .join(" ");

  const styleForGridRow = sizeToTemplateRowHeight[size];
  const styleForHeaderRow = sizeToTemplateHeaderRowHeight[size];

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  // The virtualizer reads the ref; the scroll watcher needs the element as
  // state so it attaches once the container exists.
  const [scrollArea, setScrollArea] = useState<HTMLDivElement | null>(null);
  const attachScrollArea = useCallback((element: HTMLDivElement | null) => {
    scrollAreaRef.current = element;
    setScrollArea(element);
  }, []);
  const shouldShowHeader =
    !hideHeader && !(hideHeaderWhenEmpty && rowsOfData.length === 0);
  const hasLoadingStateContent = Boolean(loadingStateContent);
  const showFullHeightState =
    rowsOfData.length === 0 &&
    (hasLoadingStateContent || Boolean(emptyStateContent));
  const fullHeightStateContent = hasLoadingStateContent
    ? loadingStateContent
    : emptyStateContent;
  const headerColumns = shouldShowHeader ? columns : [];
  // The table scrolls inside its own container, so callers that load more
  // rows on scroll hear about its edges from here, not from the page.
  const scrolled = useScrolled(scrollArea);
  useEffect(() => {
    // Before layout the container is 0px tall and so trivially "at the
    // bottom"; rows that fit without scrolling count as the bottom instead.
    if (!scrollArea || scrollArea.clientHeight === 0) {
      return;
    }
    const canScroll = scrollArea.scrollHeight > scrollArea.clientHeight;
    onVisibleHandlers?.setBottomVisible(
      canScroll ? scrolled.bottom : rowsOfData.length > 0,
    );
    onVisibleHandlers?.setTopVisible(scrolled.top);
  }, [scrollArea, scrolled, onVisibleHandlers, rowsOfData.length]);

  const rowHeightPx = parseInt(styleForGridRow?.toString() || "48");
  const headerRowHeightPx = parseInt(styleForHeaderRow?.toString() || "36");

  const calculatedAvailableHeight = useMaxAvailableHeight({
    areaRef: scrollAreaRef,
    contentBelowRef: footerRef,
  });

  const availableHeight = maxAvailableHeight ?? calculatedAvailableHeight;
  const scrollContainerStyle: React.CSSProperties =
    standalone || fillContainer
      ? { flex: 1, minHeight: 0 }
      : { height: `${availableHeight}px` };

  const totalRowCount = rowsOfData.reduce((total, _, index) => {
    const numExpansions = rowsExpansions?.[index]?.expanded
      ? rowsExpansions[index].numExpansions || 0
      : 0;
    return total + 1 + numExpansions;
  }, 0);

  const rowMapping: {
    dataIndex: number;
    isExpanded: boolean;
    expansionIndex: number;
  }[] = [];

  for (const [dataIndex, _] of rowsOfData.entries()) {
    rowMapping.push({ dataIndex, isExpanded: false, expansionIndex: -1 });

    if (rowsExpansions?.[dataIndex]?.expanded) {
      const numExpansions = rowsExpansions[dataIndex].numExpansions || 0;
      for (let i = 0; i < numExpansions; i++) {
        rowMapping.push({ dataIndex, isExpanded: true, expansionIndex: i });
      }
    }
  }

  const rowVirtualizer = useVirtualizer({
    count: showFullHeightState ? 0 : totalRowCount,
    getScrollElement: () => scrollAreaRef.current,
    estimateSize: () => rowHeightPx,
    overscan: 10,
    paddingStart: shouldShowHeader ? headerRowHeightPx : 0,
  });

  if (showFullHeightState) {
    return (
      <div
        className={classNames(redoTableCss.virtualTableContainer)}
        data-arbiter="RedoVirtualTable"
        data-scroll-container="virtual-table"
        ref={attachScrollArea}
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          ...scrollContainerStyle,
        }}
      >
        <div
          className={classNames(
            redoTableCss.table,
            additionalClasses,
            coreRedoTableCss.table,
            coreRedoTableCss.fullHeightState,
          )}
          role="table"
          style={{
            gridTemplateColumns: styleForGridColTemplate,
            gridTemplateRows: shouldShowHeader
              ? `${styleForHeaderRow} minmax(0, 1fr)`
              : "minmax(0, 1fr)",
            flex: 1,
            height: "100%",
            minHeight: 0,
            width: "100%",
          }}
        >
          <div
            aria-hidden={shouldShowHeader ? undefined : true}
            className={redoTableCss.rowWrapper}
            key="header"
            role={shouldShowHeader ? "row" : undefined}
          >
            {headerColumns.map((column, columnIndex) => {
              const stickyLeftOffset = column.sticky
                ? columns
                    .slice(0, columnIndex)
                    .filter((c) => c.sticky)
                    .reduce((acc, c) => {
                      const width = c.widthMode;
                      if (width?.type === "fixed") {
                        return (
                          acc +
                          (typeof width.width === "number"
                            ? width.width
                            : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                              parseInt(width.width as string) || 0)
                        );
                      }
                      if (
                        width?.type === "fill" &&
                        width.minLength !== undefined
                      ) {
                        return (
                          acc +
                          (typeof width.minLength === "number"
                            ? width.minLength
                            : parseInt(width.minLength as string) || 0)
                        );
                      }
                      return acc;
                    }, 0)
                : undefined;

              return (
                <div
                  className={classNames(
                    redoTableCellsCss.cell,
                    redoTableCss.headerCell,
                    headerColorStyle[headerColor],
                    column.sticky && redoTableCss.stickyColumnHeaderCell,
                  )}
                  key={`header-${column.key ?? columnIndex}`}
                  role="columnheader"
                  style={{
                    height: styleForHeaderRow,
                    ...(stickyLeftOffset !== undefined && {
                      left: `${stickyLeftOffset}px`,
                    }),
                  }}
                >
                  <column.renderHeaderCell />
                </div>
              );
            })}
          </div>
          <div className={redoTableCss.rowWrapper} role="row">
            <div
              className={coreRedoTableCss.stateCell}
              role="cell"
              style={{ gridColumn: "1 / -1" }}
            >
              {fullHeightStateContent}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        redoTableCss.virtualTableContainer,
        standalone && redoTableCss.virtualTableContainerFill,
      )}
      data-arbiter="RedoVirtualTable"
      data-scroll-container="virtual-table"
      ref={attachScrollArea}
      style={scrollContainerStyle}
    >
      <div
        className={classNames(redoTableCss.table, additionalClasses)}
        role="table"
        style={{
          flexShrink: standalone ? 0 : undefined,
          gridTemplateColumns: styleForGridColTemplate,
          gridTemplateRows: shouldShowHeader ? styleForHeaderRow : undefined,
          gridAutoRows: styleForGridRow,
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
      >
        <div
          aria-hidden={shouldShowHeader ? undefined : true}
          className={redoTableCss.rowWrapper}
          key="header"
          role={shouldShowHeader ? "row" : undefined}
        >
          {headerColumns.map((column, columnIndex) => {
            const stickyLeftOffset = column.sticky
              ? columns
                  .slice(0, columnIndex)
                  .filter((c) => c.sticky)
                  .reduce((acc, c) => {
                    const width = c.widthMode;
                    if (width?.type === "fixed") {
                      return (
                        acc +
                        (typeof width.width === "number"
                          ? width.width
                          : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                            parseInt(width.width as string) || 0)
                      );
                    }
                    if (
                      width?.type === "fill" &&
                      width.minLength !== undefined
                    ) {
                      return (
                        acc +
                        (typeof width.minLength === "number"
                          ? width.minLength
                          : parseInt(width.minLength as string) || 0)
                      );
                    }
                    return acc;
                  }, 0)
              : undefined;

            return (
              <div
                className={classNames(
                  redoTableCellsCss.cell,
                  redoTableCss.headerCell,
                  headerColorStyle[headerColor],
                  column.sticky && redoTableCss.stickyColumnHeaderCell,
                )}
                key={`header-${column.key ?? columnIndex}`}
                role="columnheader"
                style={{
                  height: styleForHeaderRow,
                  ...(stickyLeftOffset !== undefined && {
                    left: `${stickyLeftOffset}px`,
                  }),
                }}
              >
                <column.renderHeaderCell />
              </div>
            );
          })}
        </div>
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const mappingInfo = rowMapping[virtualRow.index];
          if (!mappingInfo) {
            return null;
          }

          const { dataIndex, isExpanded, expansionIndex } = mappingInfo;
          const row = rowsOfData[dataIndex];

          if (isExpanded) {
            const rowKey = `row-${dataIndex}-exp${expansionIndex}`;
            return (
              <div
                className={classNames(
                  redoTableCss.virtualRowWrapper,
                  redoTableCss.expandedRowWrapper,
                )}
                key={rowKey}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  gridTemplateColumns: styleForGridColTemplate,
                  height: `${rowHeightPx}px`,
                }}
              >
                <RedoTableExpandedRow<DATA>
                  columns={columns}
                  expansionIndex={expansionIndex}
                  key={rowKey}
                  onRowClick={onRowClick}
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  row={row}
                  rowIndex={dataIndex}
                />
              </div>
            );
          }

          return (
            <div
              className={classNames(redoTableCss.virtualRowWrapper)}
              key={`row-${dataIndex}`}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                gridTemplateColumns: styleForGridColTemplate,
                height: `${rowHeightPx}px`,
              }}
            >
              <RedoTableRow<DATA>
                columns={columns}
                isRowClickable={isRowClickable}
                key={`row-${dataIndex}`}
                onContextMenu={onContextMenu}
                onRowClick={onRowClick}
                onRowHovered={onRowHovered}
                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                row={row}
                rowIndex={dataIndex}
                rowSelected={rowsSelected?.[dataIndex]}
              />
            </div>
          );
        })}
      </div>
      {aggregationRow && (
        <div
          className={redoTableCss.stickyAggregationRow}
          style={{ gridTemplateColumns: styleForGridColTemplate }}
        >
          {aggregationRow}
        </div>
      )}
    </div>
  );
});
